'use client';

import type {
  ActionKitErrorBase,
  OnErrorHandlers,
  OnSuccessHandler,
  ActionKitErrorUnknown,
  ActionKitAction2,
} from '../action-kit';
import { useActionKit } from '../action-kit';
import type {
  ActionKitImpErrorUnauthenticated,
  ActionKitImpOptionsOnError,
} from './types';
import { useCallback, useMemo } from 'react';
import { addToast } from '@octano/global-ui';

export function useActionKitImp<
  Params extends any[],
  Return,
  TError extends ActionKitErrorBase = ActionKitErrorUnknown
>(
  action: ActionKitAction2<Params, Return, TError>,
  options?: {
    onSuccess?: OnSuccessHandler<Params, Return>;
  } & ActionKitImpOptionsOnError<TError>,
) {
  const onUnauthenticatedError = useCallback<
    OnErrorHandlers<ActionKitImpErrorUnauthenticated>['unauthenticated']
  >(
    (error) => {
      const handleProp = options?.onError?.['unauthenticated' as const];
      if (handleProp) {
        return handleProp(error);
      }
      addToast({
        text: 'Tu sesión ha expirado',
        color: 'danger',
        icon: 'error',
      });
    },
    [options?.onError],
  );

  const optionsOnError = useMemo(() => {
    return {
      ...options?.onError,
      unauthenticated: onUnauthenticatedError,
      unknown: (error) => {
        if (
          error.data instanceof Error &&
          error.data.message === 'Unauthenticated'
        ) {
          return onUnauthenticatedError({
            code: 'unauthenticated' as const,
            data: error.data,
          });
        }
        const handleProp = options?.onError?.['unknown' as const];
        if (handleProp) {
          return handleProp(error);
        }
        addToast({
          text: 'Ha ocurrido un error desconocido',
          color: 'danger',
          icon: 'error',
        });
      },
    } as OnErrorHandlers<ActionKitErrorUnknown | TError>;
  }, [options?.onError, onUnauthenticatedError]);

  return useActionKit(action, {
    onSuccess: options?.onSuccess,
    onError: optionsOnError,
  });
}
